import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useIntl } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import { formatMoney } from '../../util/currency';
import { computeBundlePricing, getBrandDisplayName } from '../../util/bundlePricing';
import { ResponsiveImage } from '../../components';

import css from './BundleListingCard.module.css';

const { Money } = sdkTypes;

/**
 * Mosaic of up to 4 product thumbnail images.
 * Layout adapts: single image, side-by-side (2), or 2×2 grid (3-4).
 */
const ImageMosaic = ({ productsWithListings, variantPrefix = 'listing-card' }) => {
  const withImages = productsWithListings
    .filter(({ listing }) => listing?.images?.[0])
    .slice(0, 4);

  const count = withImages.length;

  if (count === 0) {
    return <div className={css.imagePlaceholder} />;
  }

  return (
    <div className={classNames(css.imageMosaic, css[`mosaic${count}`])}>
      {withImages.map(({ listing, product }, idx) => {
        const img = listing.images[0];
        const title = listing?.attributes?.title || '';
        return (
          <div key={`${product.id}-${idx}`} className={css.mosaicCell}>
            <ResponsiveImage
              rootClassName={css.mosaicImage}
              alt={title}
              image={img}
              variants={[
                `${variantPrefix}`,
                `${variantPrefix}-2x`,
                'listing-gallery',
                'listing-gallery-2x',
              ]}
            />
          </div>
        );
      })}
    </div>
  );
};

/**
 * Compact card for the bundles grid page.
 * Shows a mosaic of product thumbnails + title + price.
 * Clicking opens the BundleDetailModal via the onOpen callback.
 */
const BundleListingCard = ({
  bundle,
  productListingsById = {},
  onOpen,
  config,
  className,
}) => {
  const intl = useIntl();
  const variantPrefix = config?.layout?.listingImage?.variantPrefix || 'listing-card';

  const pricing = useMemo(
    () => computeBundlePricing(bundle, productListingsById),
    [bundle, productListingsById]
  );

  const finalMoney = useMemo(() => {
    if (!pricing.currency || !Number.isFinite(pricing.finalAmount)) return null;
    try {
      return new Money(pricing.finalAmount, pricing.currency);
    } catch {
      return null;
    }
  }, [pricing.currency, pricing.finalAmount]);

  const rawMoney = useMemo(() => {
    if (!pricing.showStrikethrough || !pricing.currency || !Number.isFinite(pricing.rawSubtotal))
      return null;
    try {
      return new Money(pricing.rawSubtotal, pricing.currency);
    } catch {
      return null;
    }
  }, [pricing.showStrikethrough, pricing.currency, pricing.rawSubtotal]);

  const badgeFormatted = useMemo(() => {
    if (
      pricing.badgeDiscountSubunits == null ||
      pricing.badgeDiscountSubunits <= 0 ||
      !pricing.currency
    )
      return null;
    try {
      return formatMoney(intl, new Money(pricing.badgeDiscountSubunits, pricing.currency));
    } catch {
      return null;
    }
  }, [intl, pricing.badgeDiscountSubunits, pricing.currency]);

  const productCount = bundle.products?.length || 0;

  // Show brand name if all products share the same brand
  const singleBrand = useMemo(() => {
    const brands = pricing.productsWithListings
      .map(({ listing }) => listing?.attributes?.publicData?.brand)
      .filter(Boolean)
      .map(b => (Array.isArray(b) ? b[0] : b))
      .filter(b => typeof b === 'string' && b);
    if (brands.length === 0) return null;
    const unique = [...new Set(brands)];
    if (unique.length !== 1) return null;
    return getBrandDisplayName(unique[0], config?.listing);
  }, [pricing.productsWithListings, config]);

  return (
    <button
      type="button"
      className={classNames(css.root, className)}
      onClick={() => onOpen && onOpen(bundle)}
      aria-label={bundle.title}
    >
      <div className={css.imageWrapper}>
        {pricing.showStrikethrough && badgeFormatted ? (
          <span className={css.discountBadge}>-{badgeFormatted}</span>
        ) : null}
        <ImageMosaic
          productsWithListings={pricing.productsWithListings}
          variantPrefix={variantPrefix}
        />
      </div>

      <div className={css.info}>
        <div className={css.topRow}>
          {singleBrand ? (
            <span className={css.brandInfo}>{singleBrand}</span>
          ) : (
            <span className={css.productCount}>
              {intl.formatMessage(
                { id: 'BundleListingCard.productCount', defaultMessage: '{count} produits' },
                { count: productCount }
              )}
            </span>
          )}
        </div>
        <h3 className={css.title}>{bundle.title}</h3>

        <div className={css.priceRow}>
          {finalMoney ? (
            <>
              <span className={css.price}>{formatMoney(intl, finalMoney)}</span>
              {pricing.showStrikethrough && rawMoney ? (
                <span className={css.oldPrice}>{formatMoney(intl, rawMoney)}</span>
              ) : null}
            </>
          ) : (
            <span className={css.pricePlaceholder}>—</span>
          )}
        </div>
      </div>
    </button>
  );
};

export default BundleListingCard;
